/**
 * WhatsApp Notification Service
 * 
 * Este archivo maneja el envío de notificaciones automáticas a WhatsApp
 * cuando ocurren eventos importantes en el Dashboard (Subida de videos, Solicitud de baja, etc).
 * 
 * PRE-REQUISITO:
 * Se requiere un endpoint de API que acepte POST requests para enviar los mensajes.
 * (Ej: Twilio, Waha, Meta Cloud API, o tu propio backend).
 */

// CONFIGURATION
// Identificadores obtenidos de tu consola Meta
const PHONE_NUMBER_ID = '786572654550146';

// ⚠️ IMPORTANTE: Este token es de larga duración (aprox 60 días)
// Si ves error 401 "Session has expired", genera un nuevo token en Meta for Developers.
// Última actualización: 05-Feb-2026 ✅ (Expira: ~05-Abr-2026)
const ACCESS_TOKEN = 'EAARvjM18RvMBQgNlaIJPC9ZAGcr3jRZBS15Ky2k2XZAd5WORUmsfVWRXdKS6NFdKSCysvKSDJHaw8w1hFvJ9r4ZBx0dx2CB4LXPL4VY1At58FwyZCDIv0rFHwpXmPhKmH9F55ZAdQZBX2OJ4iu29ZAP3LW6PELi4QlM7wWx5Qc0A6ZBBvD3Wt5nUnhE4B7rk6p85r1aCa3akyUt51qJmOZAxJjLAiEyksCO9VxIoMe1kixBy4FZADZA7IuPWprkkVkjSnPiVKlyilavJr2ZAHtfTQ3GfqljZCH';

const API_ENDPOINT = `https://graph.facebook.com/v22.0/${PHONE_NUMBER_ID}/messages`;

// Número de destino (Jsalamea) 
const ADMIN_PHONE = '593982994558'; // Jsalamea

// ============================================
// NOTIFICATION TRACKING SYSTEM
// ============================================

/**
 * Obtiene las estadísticas de notificaciones desde localStorage
 */
export const getNotificationStats = () => {
    try {
        const stats = localStorage.getItem('whatsapp_notification_stats');
        if (stats) {
            return JSON.parse(stats);
        }
    } catch (error) {
        console.error('Error reading notification stats:', error);
    }

    // Default stats
    return {
        messagesSent: 0,
        messagesOpened: 0,
        lastUpdated: new Date().toISOString()
    };
};

/**
 * Incrementa el contador de mensajes enviados
 */
const incrementSent = () => {
    const stats = getNotificationStats();
    stats.messagesSent += 1;
    stats.lastUpdated = new Date().toISOString();
    localStorage.setItem('whatsapp_notification_stats', JSON.stringify(stats));
    console.log(`📊 Notificaciones enviadas: ${stats.messagesSent}`);
};

/**
 * Incrementa el contador de mensajes abiertos (simulado por ahora)
 * En producción, esto vendría de un webhook de Meta
 */
const incrementOpened = () => {
    const stats = getNotificationStats();
    stats.messagesOpened += 1;
    stats.lastUpdated = new Date().toISOString();
    localStorage.setItem('whatsapp_notification_stats', JSON.stringify(stats));
};

/**
 * Envía una notificación a WhatsApp usando una plantilla de Meta Cloud.
 * @param {string} templateName - El nombre de la plantilla registrada en Meta.
 * @param {Array} parameters - Lista de valores para las variables {{1}}, {{2}}, etc.
 */
const sendWhatsAppTemplate = async (templateName, parameters = []) => {
    try {
        console.log(`[WhatsApp Meta] Enviando plantilla "${templateName}" a ${ADMIN_PHONE}...`);

        if (!ACCESS_TOKEN || ACCESS_TOKEN === 'TU_ACCESS_TOKEN_AQUI') {
            console.warn("⚠️ WhatsApp: No se ha configurado el ACCESS_TOKEN");
            return false;
        }

        // Convertir parámetros al formato de Meta
        const components = [
            {
                type: "body",
                parameters: parameters.map(value => ({
                    type: "text",
                    text: String(value)
                }))
            }
        ];

        const response = await fetch(API_ENDPOINT, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${ACCESS_TOKEN}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                messaging_product: "whatsapp",
                to: ADMIN_PHONE,
                type: "template",
                template: {
                    name: templateName,
                    language: { code: "es" }, // Español (debe coincidir con el idioma en Meta)
                    components: components
                }
            })
        });

        const data = await response.json();

        if (!response.ok) {
            console.group("❌ Error API WhatsApp (Template)");
            console.error("Status:", response.status);
            console.error("Detalles:", data);
            console.groupEnd();
            return false;
        }

        console.log(`✅ Plantilla "${templateName}" enviada con éxito`);

        // Incrementar contador de mensajes enviados
        incrementSent();

        // Simular apertura de mensaje (70% de probabilidad)
        // En producción, esto vendría de un webhook de Meta
        setTimeout(() => {
            if (Math.random() > 0.3) {
                incrementOpened();
                console.log('📱 Mensaje abierto (simulado)');
            }
        }, 2000 + Math.random() * 3000); // Entre 2-5 segundos

        return true;
    } catch (error) {
        console.error("❌ Error enviando plantilla:", error);
        return false;
    }
};

/**
 * Notifica que un usuario de Marketing ha subido un nuevo video.
 * Usa la plantilla 'notificacion_general' configurada en Facebook Business Manager.
 */
export const notifyNewVideo = async (videoData, user) => {
    const dateTime = `${new Date().toLocaleDateString('es-EC')} ${new Date().toLocaleTimeString('es-EC')}`;

    console.log('🚀 Enviando notificación de nuevo video:', {
        usuario: user.name,
        titulo: videoData.titulo,
        cliente: videoData.cliente,
        fecha: dateTime
    });

    // Parámetros para la plantilla notificacion_general
    // Ajusta según las variables {{1}}, {{2}}, etc. que definiste en Meta
    const parameters = [
        user.name || 'Usuario',
        videoData.titulo || 'Sin título',
        videoData.cliente || 'No especificado',
        dateTime
    ];

    return sendWhatsAppTemplate("notificacion_general", parameters);
};

/**
 * Notifica una solicitud de baja.
 * Usa la plantilla 'solicitud_baja' configurada en Facebook Business Manager.
 */
export const notifyDeactivation = async (videoData, user, reason = 'No especificada') => {
    console.log('🛑 Enviando notificación de solicitud de baja:', {
        usuario: user.name,
        titulo: videoData.titulo,
        motivo: reason
    });

    // Parámetros para la plantilla solicitud_baja
    const parameters = [
        user.name || 'Usuario',
        videoData.titulo || 'Sin título',
        reason
    ];

    return sendWhatsAppTemplate("solicitud_baja", parameters);
};

export default {
    notifyNewVideo,
    notifyDeactivation
};
